/**
 * The controls on decisions (conditions 2.13 and 2.15; scenarios 6.28–6.35): the check before signature, the not-signable marking, the legal basis
 * of a decision as at its date, the files still open at a decision stage, and the Department's approval of the templates, the delegation register
 * and the legal rules.
 */
export default {
  readiness: {
    title: "Before signature",
    hint: "Checked on the draft as it stands: the wording's approval, every provision cited, the delegation in force today, the conditions, the not-signable marking and the consistency with the approved analysis.",
    checking: "Checking the draft…",
    signable: "Nothing stands in the way of signature.",
    blocked: "The decision cannot be signed yet",
    notes: "For the signer's attention",
    template: "Template",
    templateLine: "{{name}} · version {{version}}",
    pending: "Version {{version}} awaits approval",
    register: "Legal rules register",
    registerLine: "Version {{version}} · {{verified}} of {{count}} provisions cited verified",
    citations: "Provisions the decision cites",
    signatory: "Signed by",
    signatoryLine: "{{capacity}} under {{authority}}",
    delegations: "Delegation register",
    delegationsLine: "Version {{version}}",
    departureReasons: "Your reasons for departing from the analysis",
    departureHint: "Recorded with the decision and in the file's history; the signed instrument records which points the decision departs on.",
    status: { Approved: "Approved", AwaitingApproval: "Awaiting the Department's approval", EditedSinceApproval: "Edited since approval", Verified: "Verified", VerificationRequired: "Verification required", NotInRegister: "Not in the register" },
  },
  hold: {
    mark: "Mark not signable",
    markTitle: "Mark the decision document not signable",
    markHint: "While the marking stands the decision is not signed, the file is not recorded as decided and an issued decision is not notified. The reason, your name and the time are kept in the file's history.",
    reason: "Why it cannot be signed",
    reasonAr: "Why it cannot be signed (Arabic)",
    marked: "Marked not signable",
    markedBy: "{{by}} · {{date}}",
    lift: "Lift the marking",
    liftTitle: "Lift the not-signable marking",
    liftReason: "Why it can now be signed",
    markedToast: "The decision document is marked not signable",
    liftedToast: "The marking is lifted",
  },
  basis: {
    open: "Legal basis as at the date of decision",
    close: "Hide the legal basis",
    built: "Template {{template}}, version {{version}} · legal rules register {{register}} · delegation register version {{delegations}}",
    recorded: "As recorded with the decision when it was signed.",
    rebuilt: "Issued before the citations were recorded: rebuilt from the register as it stood on {{date}}.",
    gazette: "Checked against: {{reference}}",
    verifiedBy: "Verified by {{by}} on {{date}}",
    departures: "Departures from the analysis, with the signer's reasons",
    word: "Download as Word",
    none: "No provision recorded.",
  },
  open: {
    tab: "Open at decision",
    hint: "Every file at a decision stage with no decision issued there, and every file whose decision document is marked not signable, with why each remains open.",
    empty: "No file is waiting at a decision stage.",
    since: "At this stage since {{date}}",
    marked: "Marked not signable",
    issued: "Decision {{number}} issued",
    range: "{{from}}–{{to}} of {{total}}",
    pageOf: "Page {{page}} of {{pages}}",
    previous: "Previous page",
    next: "Next page",
  },
  letters: {
    title: "Cites provisions not yet verified by the Department",
    hint: "This letter is held: it cites provisions the Department has not yet checked against the Arabic text in the Official Gazette. Read it with the citations below, then confirm it – the provisions are recorded with the sending – or cancel it. The statutory periods run as they do; the party's own period runs from your confirmation.",
    sentHint: "This letter was sent citing provisions the Department had not yet checked against the Arabic text in the Official Gazette; the sending officer's confirmation and the provisions are recorded in the file's history.",
    writtenOut: "Its template writes these articles out instead of citing them from the legal rules register: {{articles}}.",
    draftHint: "Before you send it: this letter cites provisions the Department has not yet checked against the Arabic text in the Official Gazette. Sent by you, it goes out, and the provisions below are recorded with the sending in the file's history.",
    checking: "Reading the letter's citations…",
  },
  method: {
    hint: "Every figure and score the platform shows is computed by the published calculation method, in versions. A version reads \"Proposed\" until the Department approves it. A legal expert, the Director, the Assistant Under-Secretary or the Under-Secretary records the Department's written approval here; it is audited, and it holds for that version only – the next version a release publishes reads \"Proposed\" again.",
    version: "Calculation method, version {{version}}",
    approved: "Approved",
    proposed: "Proposed",
    reference: "Reference of the written approval",
    referenceHint: "The Department's letter or memorandum and its number.",
    referenceAr: "Reference in Arabic (optional)",
    approvedOn: "Date of the written approval",
    recordedBy: "Recorded by",
    note: "Note (optional)",
    notApproved: "Version {{version}} awaits the Department's approval: until it is recorded, every figure computed by it reads \"Proposed\".",
    earlierApproved: "Version {{earlier}} was approved ({{reference}}, {{date}}). The method in force is now version {{version}}, which reads \"Proposed\" until the Department approves it.",
    pendingRelease: "A release prepares version {{version}} ({{status}}): it is approved on its own once it is published.",
    releaseStatus: { Draft: "draft", PendingApproval: "awaiting a second administrator", Approved: "approved, not yet published", Scheduled: "scheduled" },
    recordTitle: "Record the Department's approval of version {{version}}",
    recordHint: "Record the approval only once the Department has given it in writing. It is audited in your name and applies to this version alone.",
    record: "Record the approval of version {{version}}",
    recorded: "The Department's approval is recorded",
    whoRecords: "The Department's approval is recorded by: {{roles}}. An administrator does not record it.",
    measures_one: "{{count}} measure of the method",
    measures_other: "{{count}} measures of the method",
  },
  register: {
    tabs: { rules: "Legal rules", templates: "Decision templates", signatories: "Signatories", glossary: "Glossary", method: "Calculation method" },
    glossary: {
      hint: "The agreed Arabic–English terms, each with its definition and source. A legal researcher, a legal expert or the Director proposes a change as a new version; a legal expert or the Director other than the one who drafted it approves it. The translation comparison and the drafting agents use the version in force.",
      inForce: "In force: version {{version}}",
      filter: "Find a term",
      none: "No term matches.",
      source: "Source",
      versions: "Versions",
      count: "{{count}} terms",
      count_one: "{{count}} term",
      programList: "The platform's list",
      approve: "Approve version {{version}}",
      approved: "Glossary version approved",
      notYours: "Approved by another officer",
      propose: "Propose a change",
      proposeTitle: "Propose a new version of the glossary",
      proposeHint: "Edit, add or remove terms. The new version waits for the approval of a legal expert or the Director other than you; until then the version in force applies.",
      termEn: "Term in English",
      termAr: "Term in Arabic",
      definitionEn: "Definition in English",
      definitionAr: "Definition in Arabic",
      sourceEn: "Source in English",
      sourceAr: "Source in Arabic",
      alsoEn: "Other English renderings (comma-separated)",
      alsoAr: "Other Arabic renderings (comma-separated)",
      remove: "Remove this term",
      add: "Add a term",
      note: "What changes and why",
      submit: "Submit for approval",
      drafted: "New version submitted for approval",
    },
    rules: {
      hint: "Each rule the platform applies and its decisions, letters, notices, clocks and procedure steps cite, with its source, the date it took effect and where the Department's verification stands. While a provision is not verified against the Arabic text in the Official Gazette: a decision citing it cannot be signed; a letter or notice citing it is held for an officer when no officer sends it, and records the provision in the file's history when an officer does; a clock keeps running under the statutory rule and shows \"not yet verified by the Department\".",
      version: "Register version {{version}}",
      filter: "Find a provision",
      asOf: "The register as at",
      cited: "Cited as",
      effective: "In force from",
      verify: "Record verification",
      verifyTitle: "Verification against the Official Gazette",
      gazette: "Official Gazette issue, date and page checked against",
      note: "Note",
      status: "Status",
      saved: "Verification recorded",
      history: "Verification history",
      historyLine: "{{by}} · {{date}} · {{status}}",
    },
    templates: {
      hint: "The wording of every decision, in versions. Wording is edited in Workflow Studio as a new version; a legal expert or the Director other than the one who drafted it approves it here. Only an approved version that has not been edited since produces a signable decision, and every printed decision shows the version used.",
      procedure: { EconomicConcentration: "Economic concentrations", Complaint: "Complaints", Whistleblowing: "Investigations and enforcement", Exemption: "Exemption applications", ExemptionAmendment: "Exemption amendments", Appeal: "Grievances" },
      inForce: "In force: version {{version}}",
      versions: "Versions",
      versionLine: "Version {{version}} · {{status}}",
      draftedBy: "Drafted by {{by}} on {{date}}",
      programWording: "The program's wording",
      approvedBy: "Approved by {{by}} on {{date}}, in force from {{from}}",
      approve: "Approve version {{version}}",
      effectiveFrom: "In force from",
      approved: "Version approved",
      block: "Block",
      wording: "Wording of version {{version}}",
    },
    signatories: {
      hint: "Who signs each decision, in what capacity and under which delegation, with the dates each is in force – acting appointments included. Changes are drafted in Workflow Studio as a new version and approved here; a decision is signed only under an approved version, by the officer a delegation in force names.",
      inForce: "Register version {{version}}",
      capacity: "Capacity",
      name: "Name",
      authority: "Authority",
      decisions: "Decisions",
      period: "In force",
      acting: "Acting",
      fromTo: "{{from}} – {{to}}",
      open: "open-ended",
      approve: "Approve version {{version}}",
      approved: "Delegation register approved",
      versions: "Versions",
      unnamed: "Named by the signing account",
    },
  },
  testEnvironment: {
    line: "Test environment – decision controls relaxed, not production behaviour:",
    codes: {
      Registers: "registers warn instead of blocking a signature",
      Heads: "the Director and above take every act and sign every decision",
      Penalty: "penalty steps open before the basis is confirmed",
      Letters: "letters citing unverified provisions are not held for that alone",
    },
    outstanding: "Approvals of the Department outstanding (test environment)",
    acknowledge: "I sign knowing these approvals are outstanding. The decision, the file's history and the PDF and Word copies will say it was signed under the test-environment controls.",
    acknowledgeNeeded: "Tick the acknowledgement to sign with these approvals outstanding.",
    signed: "Signed under the test-environment decision controls",
    signedOutstanding: "Outstanding at signature: {{list}}",
    acknowledgedBy: "Acknowledged by {{name}}",
  },
} as const;
